import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Client } from '../types/Client';
import { BuilderType, projectsCacheTags } from './projectsApiHelpers';
import { basicHeaders } from '../utils';

export interface UserDetails {
  ui_details: UIDetails;
  accepted_terms_version: string;
}
export interface UIDetails {
  theme: 'light' | 'dark';
  defaultDashboard?: number;
  monitorDefaultPeriod?: 'today' | 'last_7_days';
}

export const userDetailsApi = createApi({
  reducerPath: 'userDetailsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_APP_PROJECTS_SERVICE}/`,
    prepareHeaders: basicHeaders,
  }),
  tagTypes: [projectsCacheTags.USER_DETAILS],
  endpoints: (builder: BuilderType) => ({
    getUserDetails: builder.query<UserDetails, void>({
      query: () => ({
        url: `service/user-details`,
        method: 'GET',
      }),
      providesTags: [projectsCacheTags.USER_DETAILS],
    }),
    updateUserDetails: builder.mutation<UserDetails, Partial<UserDetails>>({
      query: (body) => ({
        url: `service/user-details`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [projectsCacheTags.USER_DETAILS],
    }),
    updateUserUIDetails: builder.mutation<UserDetails, UserDetails>({
      query: (body) => ({
        url: `service/user-details/ui`,
        method: 'PUT',
        body,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          userDetailsApi.util.updateQueryData('getUserDetails', undefined, (draft: any) => {
            Object.assign(draft, { ui_details: arg });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [projectsCacheTags.USER_DETAILS],
    }),
    getUserActiveClient: builder.query<Client, void>({
      query: () => ({
        url: `service/user-active-client`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetUserDetailsQuery,
  useLazyGetUserDetailsQuery,
  useUpdateUserDetailsMutation,
  useUpdateUserUIDetailsMutation,
  useGetUserActiveClientQuery,
} = userDetailsApi;
